import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  LogOut, Plus, Trash2, DollarSign, TrendingUp, Briefcase, Target,
  BarChart3, Building2, ArrowUpRight, Eye, Activity
} from "lucide-react";
import AnalyticsTab from "@/components/AnalyticsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";
const CHART_COLORS = ["#1e3a8a", "#1e40af", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"];
const TOOLTIP_STYLE = { fontSize: 12, fontFamily: "IBM Plex Sans" };

const fmt = (n) => {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const STAGES = ["Sourcing", "Diligence", "IC Approval", "Deployed"];
const STAGE_COLORS = {
  Sourcing: "bg-slate-100 text-slate-700",
  Diligence: "bg-amber-50 text-amber-700",
  "IC Approval": "bg-blue-50 text-blue-700",
  Deployed: "bg-emerald-50 text-emerald-700",
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [funds, setFunds] = useState([]);
  const [deals, setDeals] = useState([]);
  const [addInvOpen, setAddInvOpen] = useState(false);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [addFundOpen, setAddFundOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, i, f, d] = await Promise.all([
        axios.get(`${API}/dashboard/summary`, { withCredentials: true }),
        axios.get(`${API}/investments`, { withCredentials: true }),
        axios.get(`${API}/funds`, { withCredentials: true }),
        axios.get(`${API}/deals`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setInvestments(i.data);
      setFunds(f.data);
      setDeals(d.data);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/login");
      }
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const deleteItem = async (type, id) => {
    try {
      await axios.delete(`${API}/${type}/${id}`, { withCredentials: true });
      toast.success("Deleted successfully");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateDealStage = async (dealId, stage) => {
    try {
      await axios.put(`${API}/deals/${dealId}/stage`, { stage }, { withCredentials: true });
      toast.success("Stage updated");
      fetchAll();
    } catch {
      toast.error("Failed to update stage");
    }
  };

  return (
    <div className="min-h-screen bg-white font-ibm" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="dashboard-header">
        <div className="max-w-[1440px] mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Berkshire" className="w-10 h-10 object-contain" />
            <span className="font-manrope font-bold text-[#0f172a] text-sm tracking-tight">Berkshire Africa Inc</span>
            <Separator orientation="vertical" className="h-5 mx-2" />
            <span className="text-slate-400 text-xs font-ibm">Portfolio Management</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 font-ibm hidden sm:block" data-testid="user-email">{user?.email}</span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-600 hover:bg-red-50 h-8 px-3"
            >
              <LogOut className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline text-xs">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 py-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="summary-cards">
            <SummaryCard icon={DollarSign} label="Total Invested" value={fmt(summary.total_invested)} testId="total-invested" />
            <SummaryCard icon={TrendingUp} label="Current Value" value={fmt(summary.total_current_value)} testId="current-value" />
            <SummaryCard icon={ArrowUpRight} label="Overall Return" value={`${summary.overall_return_pct}%`} accent testId="overall-return" />
            <SummaryCard icon={Target} label="Active Deals" value={summary.total_deals} testId="total-deals" />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="portfolio" className="space-y-4" data-testid="dashboard-tabs">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Portfolio
            </TabsTrigger>
            <TabsTrigger value="funds" data-testid="tab-funds" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Funds
            </TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Pipeline
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs font-manrope font-semibold data-[state=active]:bg-white">
              <Activity className="w-3.5 h-3.5 mr-1.5" /> Analytics
            </TabsTrigger>
          </TabsList>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" data-testid="portfolio-tab-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              {/* Allocation Chart */}
              {summary?.sector_allocation?.length > 0 && (
                <Card className="border-slate-200" data-testid="allocation-chart-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Sector Allocation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={summary.sector_allocation} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} strokeWidth={2} stroke="#fff">
                          {summary.sector_allocation.map((entry, idx) => (
                            <Cell key={entry.name} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => fmt(v)} contentStyle={TOOLTIP_STYLE} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                      {summary.sector_allocation.map((s, i) => (
                        <div key={s.name} className="flex items-center gap-1.5 text-xs text-slate-600 font-ibm">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          {s.name}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quick Stats */}
              <Card className="border-slate-200 lg:col-span-2" data-testid="investments-summary-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Investments</CardTitle>
                  <Dialog open={addInvOpen} onOpenChange={setAddInvOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-investment-button" size="sm" className="h-7 text-xs bg-[#1e3a8a] hover:bg-[#1e40af]">
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-manrope">Add Investment</DialogTitle>
                      </DialogHeader>
                      <AddInvestmentForm onSuccess={() => { setAddInvOpen(false); fetchAll(); }} />
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Name</TableHead>
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Sector</TableHead>
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-right">Invested</TableHead>
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-right">Current</TableHead>
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-right">Return</TableHead>
                          <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-center">Details</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {investments.map((inv) => (
                          <TableRow key={inv.id} className="hover:bg-slate-50/50" data-testid={`investment-row-${inv.id}`}>
                            <TableCell className="font-ibm text-sm text-slate-800 font-medium">{inv.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs font-ibm">{inv.sector}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-ibm-mono text-sm">{fmt(inv.invested_amount)}</TableCell>
                            <TableCell className="text-right font-ibm-mono text-sm">{fmt(inv.current_value)}</TableCell>
                            <TableCell className="text-right font-ibm-mono text-sm text-emerald-600">+{inv.returns_pct}%</TableCell>
                            <TableCell className="text-center">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-[#1e3a8a]" onClick={() => navigate(`/dashboard/business/${inv.id}`)} data-testid={`view-inv-${inv.id}`}>
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => deleteItem("investments", inv.id)} data-testid={`delete-inv-${inv.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Funds Tab */}
          <TabsContent value="funds" data-testid="funds-tab-content">
            <div className="flex justify-end mb-4">
              <Dialog open={addFundOpen} onOpenChange={setAddFundOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-fund-button" size="sm" className="h-8 text-xs bg-[#1e3a8a] hover:bg-[#1e40af]">
                    <Plus className="w-3 h-3 mr-1" /> Add Fund
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-manrope">Add Fund</DialogTitle>
                  </DialogHeader>
                  <AddFundForm onSuccess={() => { setAddFundOpen(false); fetchAll(); }} />
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {funds.map((fund) => (
                <Card key={fund.id} className="border-slate-200 hover:shadow-md transition-shadow duration-200" data-testid={`fund-card-${fund.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-base font-manrope font-bold text-slate-800 leading-tight">{fund.name}</CardTitle>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500 shrink-0" onClick={() => deleteItem("funds", fund.id)} data-testid={`delete-fund-${fund.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <Badge variant="outline" className="text-xs w-fit mt-1">{fund.status}</Badge>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <MetricItem label="Fund Size" value={fmt(fund.fund_size)} />
                      <MetricItem label="Deployed" value={fmt(fund.deployed_capital)} />
                      <MetricItem label="IRR" value={`${fund.irr}%`} accent />
                      <MetricItem label="MOIC" value={`${fund.moic}x`} />
                      <MetricItem label="DPI" value={`${fund.dpi}x`} />
                      <MetricItem label="TVPI" value={`${fund.tvpi}x`} />
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-400 font-ibm mb-1">Vintage {fund.vintage_year}</p>
                      <div className="flex flex-wrap gap-1">
                        {fund.sector_focus?.map((s) => (
                          <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-ibm">{s}</span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline" data-testid="pipeline-tab-content">
            <Card className="border-slate-200">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-manrope font-semibold text-slate-700">Deal Pipeline</CardTitle>
                <Dialog open={addDealOpen} onOpenChange={setAddDealOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-deal-button" size="sm" className="h-7 text-xs bg-[#1e3a8a] hover:bg-[#1e40af]">
                      <Plus className="w-3 h-3 mr-1" /> Add Deal
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-manrope">Add Deal</DialogTitle>
                    </DialogHeader>
                    <AddDealForm onSuccess={() => { setAddDealOpen(false); fetchAll(); }} />
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Deal</TableHead>
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Company</TableHead>
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Sector</TableHead>
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider">Stage</TableHead>
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-right">Size</TableHead>
                        <TableHead className="text-xs font-manrope font-semibold text-slate-500 uppercase tracking-wider text-right">Exp. Return</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deals.map((deal) => (
                        <TableRow key={deal.id} className="hover:bg-slate-50/50" data-testid={`deal-row-${deal.id}`}>
                          <TableCell className="font-ibm text-sm text-slate-800 font-medium">{deal.name}</TableCell>
                          <TableCell className="text-sm text-slate-600 font-ibm">{deal.company}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs font-ibm">{deal.sector}</Badge>
                          </TableCell>
                          <TableCell>
                            <Select value={deal.stage} onValueChange={(v) => updateDealStage(deal.id, v)}>
                              <SelectTrigger className={`h-7 w-[130px] text-xs font-ibm border-0 ${STAGE_COLORS[deal.stage] || "bg-slate-100 text-slate-700"}`} data-testid={`deal-stage-select-${deal.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STAGES.map((s) => (
                                  <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right font-ibm-mono text-sm">{fmt(deal.deal_size)}</TableCell>
                          <TableCell className="text-right font-ibm-mono text-sm text-emerald-600">{deal.expected_return}%</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => deleteItem("deals", deal.id)} data-testid={`delete-deal-${deal.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="analytics-tab-wrapper">
            <AnalyticsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ── Sub Components ── */

function SummaryCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="border-slate-200" data-testid={`summary-${testId}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${accent ? "bg-emerald-50" : "bg-slate-100"}`}>
          <Icon className={`w-4 h-4 ${accent ? "text-emerald-600" : "text-[#1e3a8a]"}`} />
        </div>
        <div>
          <p className="text-xs text-slate-500 font-manrope">{label}</p>
          <p className={`text-lg font-manrope font-bold tracking-tight ${accent ? "text-emerald-600" : "text-slate-800"}`} data-testid={testId}>
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricItem({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 font-ibm uppercase tracking-wider">{label}</p>
      <p className={`text-sm font-manrope font-bold ${accent ? "text-emerald-600" : "text-slate-800"}`}>{value}</p>
    </div>
  );
}

function AddInvestmentForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", sector: "Trade Finance", invested_amount: "", current_value: "", returns_pct: "0", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/investments`, {
        ...form,
        invested_amount: parseFloat(form.invested_amount),
        current_value: parseFloat(form.current_value),
        returns_pct: parseFloat(form.returns_pct),
        date_invested: new Date().toISOString().split("T")[0],
      }, { withCredentials: true });
      toast.success("Investment added");
      onSuccess();
    } catch {
      toast.error("Failed to add investment");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <Label className="text-xs font-ibm">Name</Label>
        <Input data-testid="inv-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-9 text-sm" />
      </div>
      <div>
        <Label className="text-xs font-ibm">Sector</Label>
        <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
          <SelectTrigger data-testid="inv-sector-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Trade Finance", "Energy", "Agriculture", "Telecom", "FMCG", "Fintech", "Infrastructure"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-ibm">Invested ($)</Label>
          <Input data-testid="inv-amount-input" type="number" value={form.invested_amount} onChange={(e) => setForm({ ...form, invested_amount: e.target.value })} required className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-ibm">Current Value ($)</Label>
          <Input data-testid="inv-current-input" type="number" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} required className="h-9 text-sm" />
        </div>
      </div>
      <Button type="submit" disabled={submitting} data-testid="inv-submit-button" className="w-full h-9 text-sm bg-[#1e3a8a] hover:bg-[#1e40af]">
        {submitting ? "Adding..." : "Add Investment"}
      </Button>
    </form>
  );
}

function AddDealForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", company: "", sector: "Trade Finance", deal_size: "", expected_return: "0", description: "", lead: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/deals`, {
        ...form,
        deal_size: parseFloat(form.deal_size),
        expected_return: parseFloat(form.expected_return),
      }, { withCredentials: true });
      toast.success("Deal added");
      onSuccess();
    } catch {
      toast.error("Failed to add deal");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <Label className="text-xs font-ibm">Deal Name</Label>
        <Input data-testid="deal-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-9 text-sm" />
      </div>
      <div>
        <Label className="text-xs font-ibm">Company</Label>
        <Input data-testid="deal-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required className="h-9 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-ibm">Sector</Label>
          <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
            <SelectTrigger data-testid="deal-sector-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Trade Finance", "Energy", "Agriculture", "Telecom", "FMCG", "Fintech", "Infrastructure"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-ibm">Deal Size ($)</Label>
          <Input data-testid="deal-size-input" type="number" value={form.deal_size} onChange={(e) => setForm({ ...form, deal_size: e.target.value })} required className="h-9 text-sm" />
        </div>
      </div>
      <Button type="submit" disabled={submitting} data-testid="deal-submit-button" className="w-full h-9 text-sm bg-[#1e3a8a] hover:bg-[#1e40af]">
        {submitting ? "Adding..." : "Add Deal"}
      </Button>
    </form>
  );
}

function AddFundForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", fund_size: "", vintage_year: "2025", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/funds`, {
        ...form,
        fund_size: parseFloat(form.fund_size),
        vintage_year: parseInt(form.vintage_year),
        deployed_capital: 0,
      }, { withCredentials: true });
      toast.success("Fund added");
      onSuccess();
    } catch {
      toast.error("Failed to add fund");
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div>
        <Label className="text-xs font-ibm">Fund Name</Label>
        <Input data-testid="fund-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-9 text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-ibm">Fund Size ($)</Label>
          <Input data-testid="fund-size-input" type="number" value={form.fund_size} onChange={(e) => setForm({ ...form, fund_size: e.target.value })} required className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-ibm">Vintage Year</Label>
          <Input data-testid="fund-vintage-input" type="number" value={form.vintage_year} onChange={(e) => setForm({ ...form, vintage_year: e.target.value })} required className="h-9 text-sm" />
        </div>
      </div>
      <Button type="submit" disabled={submitting} data-testid="fund-submit-button" className="w-full h-9 text-sm bg-[#1e3a8a] hover:bg-[#1e40af]">
        {submitting ? "Adding..." : "Add Fund"}
      </Button>
    </form>
  );
}
